import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import BuildingCarousel, {
  BuildingCarouselHandle,
} from '../Building/BuildingCarousel';
import { flattenStrapiBulkDataItems } from '../../lib/flattenStrapiBulkDataItems';
import TableUnit from './TableUnit';
import LineBetweenSections from '../LineBetweenSections';
import AboutUnit from './AboutUnit';
import KeyFeatures from './KeyFeatures';
import UnitFeatures from './UnitFeatures';
import AdditionalUnitDetails from './AdditionalUnitDetails';
import UnitFloorplanSection from './UnitFloorplanSection';
import FinancingSection from 'components/Building/FinancingSection';
import RequestInfo from '../RequestInfo';
import PriceHistory from './PriceHistory';
import CondoValueEstimates from './CondoValueEstimates/CondoValueEstimates';
import MonthlyCostContainer from './MonthlyCost/MonthlyCostContainer';
import AboutSection from './AboutSection';
import RecentSales from '../RecentSales';
import { UNIT_DISPLAY_COUNTER } from '../../src/constants/constants';
import {
  getATTFromLocalStorage,
  wasOnceLogged,
  getListingUnitIdentifier,
} from '../../src/utils/utils';
import ErrorBoundary from '../ErrorBoundary';
import { processImages } from 'lib/getImages';

import {
  UnitPageContext,
  UnitPageContextInterface,
} from '../../pages/[urlParam]/[urlBuildParam]/[urlUnitParam]';
import { Metadata } from 'components/Metadata';
import { getStrapiMedia } from 'lib/getStrapiMedia';
import { useAuth } from 'components/Auth/AuthProvider';
import { isListingOffMarket } from 'lib/checkListingStatus';
import ContentGroupTracker from 'src/gtm/ContentGroupTracker';
import { trackViewContent } from 'src/metaPixel';
import { useRouter } from 'next/router';
import { usePPC } from 'components/PPCProvider';
import { useVisibleSection } from '../../hooks/useVisibleSection';
import {
  HEADER_TRANSITION,
  HEADER_HEIGHT,
} from 'components/layouts/Header/Header';
import { PageLayout } from 'components/layouts/PageLayout';
import { RelatedArticlesSection } from 'components/RelatedArticlesSection';
import { UnitPageHeader } from './UnitPageHeader';
import UnitGalleryPopupHeaderV2 from 'componentsV2/layoutsV2/HeaderV2/GalleryPopupHeaderV2/UnitGalleryPopupHeaderV2';
import { useScrollController } from 'hooks/useScrollController';
import { useExperts } from '../../hooks/useExperts';

const UnitPage: React.FC = () => {
  const { asPath } = useRouter();
  const { isPPC } = usePPC();
  const { openHeaderPopup, me, isLogged } = useAuth();
  const { shouldShow } = useScrollController({ threshold: 100 });

  const { building, unit, listing, blogs, neighborhood, url, city } =
    useContext(UnitPageContext) as UnitPageContextInterface;

  const { experts } = useExperts();

  const buildingCarouselRef = useRef<BuildingCarouselHandle>(null);

  const { propertyTypeCode, statusCode } = listing;

  const isUnitInPreconstruction =
    listing?.unit?.data?.attributes?.building?.data?.attributes
      ?.isPreconstruction;
  const hideAvmAndHistory = listing?.unit?.data?.attributes?.hideAvmAndHistory;
  const isForRent = Boolean(propertyTypeCode === 'R');
  const isActiveProperty = !isListingOffMarket(statusCode);
  let forRentOrSale = isActiveProperty
    ? isForRent
      ? 'for Rent'
      : 'for Sale'
    : '';
  const unitIdentifier = getListingUnitIdentifier(listing);
  const title = `${building.name} Unit #${unitIdentifier} ${forRentOrSale} in ${neighborhood.name} - ${city.name} | CondoBlackBook`;
  const unitName = `${building.name} #${unitIdentifier}`;
  const description = `Explore ${building.name} Unit #${unitIdentifier} ${forRentOrSale} at ${building.primaryAddress} in ${neighborhood.name} - ${city.name}, with real-time availability, photos, floor plans, price history, search similar condos for sale or rent, and latest news.`;
  const images = flattenStrapiBulkDataItems(listing.images?.data);
  const processedImages = processImages(
    images,
    `${building.name} #${unitIdentifier}`
  );
  const [isIconPPC, setIconIsPPC] = useState(false);

  useEffect(() => {
    checkPPCAttribute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPPC, asPath]);

  // UnitPage stays mounted when navigating unit -> unit on the client, so key
  // on the listing id to send exactly one ViewContent per unit page view.
  useEffect(() => {
    trackViewContent({
      listingId: listing.mlsListingId,
      name: unitName,
      price: listing.price,
      city: city?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.mlsListingId]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  function checkPPCAttribute() {
    const att = getATTFromLocalStorage();
    const unitDisplayCounter = localStorage.getItem(UNIT_DISPLAY_COUNTER);
    if (!me?.id && !wasOnceLogged() && att && unitDisplayCounter) {
      if (!isLogged && att <= unitDisplayCounter) {
        openHeaderPopup({ keepOpen: true });
      } else {
        localStorage.setItem(
          UNIT_DISPLAY_COUNTER,
          String(Number(unitDisplayCounter) + 1)
        );
      }
    }
  }

  const showVirtualTour = !!listing.virtualTourUrl
    ? (listing.statusCode === 'A' ||
      listing.statusCode === 'AC' ||
      listing.statusCode === 'PS') &&
      (!listing.virtualTourUrl.includes('propertypanorama') ||
        !listing.virtualTourUrl.includes('immoviewer') ||
        !listing.virtualTourUrl.includes('corelistingmachine')) &&
      listing.virtualTourUrl !== null
      ? listing.virtualTourUrl
      : ''
    : '';
  const show3DWalkthrough =
    !!showVirtualTour &&
    !showVirtualTour.startsWith('http') &&
    `https://${showVirtualTour}`;

  const hiddenListings = [
    'A10378194',
    'A10240593',
    'F10309906',
    'F10203206',
    'F10326762',
    'A11202780',
    'A10962840',
    'A10797965',
    'A11154866',
    'A10948054',
    'A11013784',
    'A10329661',
    'A10274004',
    'A11118310',
    'A11088933',
    'A10750819',
    'F10305805',
    'F10305981',
    'F10306117',
    'A10967035',
    'F10160993',
    'F10234985',
    'A10719662',
    'A10754336',
    'F10309986',
    'F10302640',
    'F10229591',
    'A11018401',
  ];
  const hiddenStatusCodes = ['C', 'W', 'X'];
  const hidePhotos =
    hiddenListings.includes(listing.mlsListingId) ||
    hiddenStatusCodes.includes(listing.statusCode);

  const RentOrSaleRef = useRef<any>(null);
  const FeaturesAndAmenitiesRef = useRef<any>(null);
  const FloorplansRef = useRef<any>(null);
  const HistoryRef = useRef<any>(null);
  const AboutNeighborhoodRef = useRef<any>(null);

  const unitNameRef = useRef<HTMLDivElement>(null);

  const linksForHeader = useMemo(
    () => [
      {
        id: 'RentOrSale',
        name: `${isForRent ? 'For Rent' : 'For Sale'}`,
        nameMobile: 'RentOrSale',
        section: 'RentOrSale',
        ref: RentOrSaleRef,
      },
      {
        id: 'FeaturesAndAmenities',
        name: 'Features & Amenities',
        nameMobile: 'Features & Amenities',
        section: 'FeaturesAndAmenities',
        ref: FeaturesAndAmenitiesRef,
      },
      {
        id: 'FloorPlan',
        name: `Floor Plan`,
        nameMobile: `Floor Plan`,
        section: 'FloorPlan',
        ref: FloorplansRef,
      },
      {
        id: 'History',
        name: 'History',
        nameMobile: 'History',
        section: 'History',
        ref: HistoryRef,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const linksForHeaderWithoutHistory = useMemo(
    () => [
      {
        id: 'RentOrSale',
        name: `${isForRent ? 'For Rent' : 'For Sale'}`,
        nameMobile: 'RentOrSale',
        section: 'RentOrSale',
        ref: RentOrSaleRef,
      },
      {
        id: 'FeaturesAndAmenities',
        name: 'Features & Amenities',
        nameMobile: 'Features & Amenities',
        section: 'FeaturesAndAmenities',
        ref: FeaturesAndAmenitiesRef,
      },
      {
        id: 'FloorPlan',
        name: `Floor Plan`,
        nameMobile: `Floor Plan`,
        section: 'FloorPlan',
        ref: FloorplansRef,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const router = useRouter();

  const isUnitPagePpc = router.query.att;
  useEffect(() => {
    if (isUnitPagePpc && +isUnitPagePpc >= 1) {
      setIconIsPPC(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sectionRefs = [
    { id: 'RentOrSale', ref: RentOrSaleRef },
    { id: 'FeaturesAndAmenities', ref: FeaturesAndAmenitiesRef },
    { id: 'FloorPlan', ref: FloorplansRef },
    { id: 'History', ref: HistoryRef },
  ];

  const visibleSection = useVisibleSection(sectionRefs);

  return (
    <>
      <Metadata
        {...{
          title,
          image: getStrapiMedia(images[0], 'small'),
          description,
          url,
        }}
      />
      <PageLayout
        title={unitName}
        links={
          listing.unit.data.attributes.history?.length > 0 &&
          !hideAvmAndHistory
            ? linksForHeader
            : linksForHeaderWithoutHistory
        }
        sectionScrollRef={RentOrSaleRef}
        visibleSection={visibleSection}
        breadcrumbs={{
          neighborhoodId: building.neighborhood.data.attributes.slug,
          neighborhoodName: building.neighborhood.data.attributes.name,
          buildingName: building.name,
          buildingId: building.slug,
          unitPage: true,
          unitName: unit.unique,
          unitUrl: url,
        }}
        scrollPastRef={unitNameRef}
        headerContent={
          <>
            <ContentGroupTracker
              pageGroup={listing?.propertyTypeCode === 'R' ? 'rent' : 'sales'}
            />
            <UnitPageHeader unitNameRef={unitNameRef} />
          </>
        }
      >
        <div
          className="sticky z-100 bg-white ml-auto md:ml-0 flex md:flex-wrap"
          style={{
            top: shouldShow ? 0 : `${HEADER_HEIGHT}px`,
            transition: `top ${HEADER_TRANSITION.duration}ms ${HEADER_TRANSITION.easing}`,
          }}
        ></div>

        <ErrorBoundary>
          <BuildingCarousel
            ref={buildingCarouselRef}
            nameButton="Floor plan"
            images={hidePhotos ? [] : processedImages}
            position={
              building.lon && building.lat
                ? {
                  lat: building.lat,
                  lng: building.lon,
                }
                : undefined
            }
            renderHeader={(onClose, photoCounter) => (
              <UnitGalleryPopupHeaderV2
                onClose={onClose}
                photoCounter={photoCounter}
              />
            )}
            tours={show3DWalkthrough || showVirtualTour}
            carouselKey={unit.id.toString()}
          />
        </ErrorBoundary>
        <div ref={RentOrSaleRef} className="relative">
          <TableUnit />
        </div>
        <LineBetweenSections hideLine={'hidden container'} />
        <AboutUnit experts={experts} isForRent={forRentOrSale === 'for Rent'} />
        <LineBetweenSections hideLine={'md:hidden container'} />
        <ErrorBoundary>
          <div className="relative">
            <div className="absolute top-[-127px] md:top-[-144px] xl:top-[-75px]" />
            <KeyFeatures
              floorplanCoordinates={FloorplansRef?.current?.offsetTop}
            />
          </div>
        </ErrorBoundary>
        <LineBetweenSections hideLine={'sm:hidden container'} />
        <div className="relative" ref={FeaturesAndAmenitiesRef}>
          <div
            id="FeaturesAndAmenities"
            className="absolute top-[60px] md:top-[-118px] xl:top-[-30px]"
          />
          <UnitFeatures />
        </div>
        <LineBetweenSections hideLine={'sm:hidden container'} />
        <AdditionalUnitDetails />
        <div className="relative" ref={FloorplansRef}>
          <div
            id="FeaturesAndAmenities"
            className="absolute top-[-63px] md:top-[-95px] xl:top-[-20px]"
          />
          <LineBetweenSections hideLine={'container pt-[40px] md:pt-[22px]'} />
          <UnitFloorplanSection
            entity={listing}
            onEnlarge={() => buildingCarouselRef.current?.openDialog(2)}
          />
        </div>
        {!isForRent && !isUnitInPreconstruction && (
          <ErrorBoundary>
            {/* Negative top margin trims the floor plan's 68px bottom padding to the design's 30px on phones */}
            <FinancingSection
              buildingName={building.name}
              lowestDownPayment={building.lowestDownPayment}
              className="mt-[-38px] mb-10 md:mt-16 lg:mt-[43px] md:mb-[200px]"
            />
          </ErrorBoundary>
        )}
        <ErrorBoundary>
          <div className="relative">
            <div className="absolute top-[-110px] md:top-[-130px] xl:-top-10" />
            <div className="relative px-0 pt-[22.5px] md:pt-[28px] mb-6 xl:px-32 text-m bg-beige-light">
              <RequestInfo
                experts={experts}
                specificPage={
                  forRentOrSale === 'for Rent'
                    ? 'RENTAL_UNIT_PAGE'
                    : 'NOT_SPECIFIC'
                }
              />
            </div>
          </div>
        </ErrorBoundary>
        {!hideAvmAndHistory && (
          <div className="relative" ref={HistoryRef}>
            <div
              id="History"
              className="absolute top-[230px] md:top-[-118px] xl:top-[-30px]"
            />
            <PriceHistory />
          </div>
        )}
        {!isUnitInPreconstruction && !hideAvmAndHistory && <CondoValueEstimates experts={experts} />}
        {(isActiveProperty || statusCode === 'PS') && !isForRent && (
          <MonthlyCostContainer />
        )}
        <div className="relative" ref={AboutNeighborhoodRef}>
          <div
            id="AboutNeighborhood"
            className="absolute top-[280px] md:top-[-130px] xl:top-[-50px]"
          />
          <AboutSection isPPC={isPPC} isIconPPC={isIconPPC} />
        </div>
        <section className="container relative md:mt-2 md:pt-0 section lg:pt-0 lg:mt-0 md:pb-0 text-m">
          <ErrorBoundary>
            <RecentSales
              name={building.name}
              urlBuildParam={building.slug}
              showLine={true}
            />
          </ErrorBoundary>
        </section>
        <RelatedArticlesSection blogs={blogs} />
      </PageLayout>
    </>
  );
};

export default UnitPage;
